import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { resolveHomePortal, PORTAL_HOME } from "@/router/portals";
import { usePortalGuard } from "@livingsyncs/ui/src/lib/crossAppRedirect";
import { Loader, Center } from "@mantine/core";

/**
 * Index-route redirect for the app portal (US-044 AC3).
 *
 *   app       → /dashboard   (org admin, facility manager, medical/care staff)
 *   no-access → /no-access   (no permissions and no facility assignment)
 *   admin/audit → this app is signed the user out (usePortalGuard) and sent
 *                 to /login with an inline error naming the right portal —
 *                 an account whose home portal isn't "app" never belongs
 *                 here, so a same-origin route can't serve it (frontend
 *                 split epic: admin/audit are their own separate apps now).
 *
 * Portal membership is decided by the single shared `resolveHomePortal`. The
 * `isLoading` guard is load-bearing (AC16): deciding on an undefined context
 * is the root cause of the /admin 403 + FacilitySwitcher flash bugs, so we
 * render a loader and make NO access decision until the context resolves.
 */
export function RoleBasedRedirect() {
  const { data: context, isLoading } = useAuth();
  const home = context ? resolveHomePortal(context) : null;
  const status = usePortalGuard(home, "app");

  if (isLoading || !context || status === "checking") {
    return (
      <Center h="100vh">
        <Loader size="xl" />
      </Center>
    );
  }

  if (status === "wrong-portal") {
    return <Navigate to="/login" state={{ wrongPortalHome: home }} replace />;
  }

  if (status === "no-access") {
    return <Navigate to={PORTAL_HOME["no-access"]} replace />;
  }

  return <Navigate to={PORTAL_HOME.app} replace />;
}
